/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from 'react';
import { useTranslation } from 'react-i18next';

interface FilterBarProps {
  batchCodes: string[];
  mediaTypes?: string[];
  selectedBatchCode: string;
  selectedStatus: string;
  selectedMediaType?: string;
  fromDate: string;
  toDate: string;
  searchTerm: string;
  onBatchCodeChange: (val: string) => void;
  onMediaTypeChange?: (val: string) => void;
  onFromDateChange: (val: string) => void;
  onToDateChange: (val: string) => void;
  onSearchChange: (val: string) => void;
  onClearFilters: () => void;
  firstFilterLabel?: string;
  firstFilterPlaceholder?: string;
  onSearchClick?: () => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  batchCodes,
  mediaTypes = [],
  selectedBatchCode,
  selectedStatus,
  selectedMediaType = '',
  fromDate,
  toDate,
  searchTerm,
  onBatchCodeChange,
  onMediaTypeChange,
  onFromDateChange,
  onToDateChange,
  onSearchChange,
  onClearFilters,
  onSearchClick,
  firstFilterLabel = "Batch",
  firstFilterPlaceholder = "Select",

}) => {
  const { t } = useTranslation();
  return (
    <div className="bg-transparent p-1 mb-1">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
        {/* First Filter (Batch Code / Lot #) */}
        <div className="flex flex-col gap-0">
          <label className="block mb-0 text-[9px] font-black text-slate-500 uppercase tracking-widest">{firstFilterLabel}</label>
          <div className="relative">
            <select
              value={selectedBatchCode}
              onChange={(e) => onBatchCodeChange(e.target.value)}
              className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-1 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-orange-500/20 outline-none appearance-none cursor-pointer"
            >
              <option value="">{firstFilterPlaceholder}</option>
              {batchCodes.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
            <span className="material-symbols-outlined text-[18px] absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">{t("mediaPowder.icons.expand_more")}</span>
          </div>
        </div>

        {/* Media Name / Type Filter */}
        <div className="flex flex-col gap-0.25">
          <label className="block mb-0 text-[9px] font-black text-slate-500 uppercase tracking-widest">{t("containerInventory.tableColumns.containerName")}</label>
          <div className="relative">
            <select
              value={selectedMediaType}
              onChange={(e) => onMediaTypeChange?.(e.target.value)}
              className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-1 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-orange-500/20 outline-none appearance-none cursor-pointer"
            >
              <option value="">{t("mediaPowder.filter_list.selectType")}</option>
              {mediaTypes.map((type: any) => (
                <option key={type.name} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
            <span className="material-symbols-outlined text-[18px] absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">{t("mediaPowder.icons.expand_more")}</span>
          </div>
        </div>

        {/* From Date Filter */}
        <div className="flex flex-col gap-0.25">
          <label className="block mb-0 text-[9px] font-black text-slate-500 uppercase tracking-widest">{t("mediaPowder.filter_list.fromDate")}</label>
          <div className="relative">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => onFromDateChange(e.target.value)}
              className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-10 py-1 text-xs font-medium text-slate-700 outline-none focus:ring-0 focus:border-slate-300 appearance-none"
            />
          </div>
        </div>

        {/* To Date Filter */}
        <div className="flex flex-col gap-0.25">
          <label className="block mb-0 text-[9px] font-black text-slate-500 uppercase tracking-widest">{t("mediaPowder.filter_list.toDate")}</label>
          <div className="relative">
            <input
              type="date"
              value={toDate}
              onChange={(e) => onToDateChange(e.target.value)}
              className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-lg px-3 py-1 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
        </div>

        {/* Generic Search */}
        <div className="flex flex-col gap-0.25">
          <label className="block mb-0 text-[9px] font-black text-slate-500 uppercase tracking-widest">{t("mediaPowder.filter_list.genericSearch")}</label>
          <div className="relative">
            {/* <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">{t("mediaPowder.filter_list.search")}</span> */}
            <input
              type="text"
              placeholder={t("mediaPowder.filter_list.search_placeholder")}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full h-[28px] bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-10 py-1 text-xs font-medium text-slate-700 focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
            <span
              className="material-symbols-outlined text-[18px] absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer"
              onClick={() => onSearchClick?.()}
            >
              {t("mediaPowder.filter_list.search")}
            </span>
          </div>
        </div>
      </div>

      {(selectedBatchCode || selectedStatus || selectedMediaType || fromDate || toDate || searchTerm) && (
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClearFilters}
            className="text-[10px] font-black text-orange-500 uppercase tracking-widest hover:text-orange-600 flex items-center gap-1 outline-none focus:outline-none focus:ring-0 border-0 shadow-none"
          >
            <span className="material-symbols-outlined text-sm">{t("mediaPowder.filter_list.filteraltoff")}</span>
            {t("mediaPowder.filter_list.clearAllFilters")}
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
